import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { ChevronLeftIcon } from '@heroicons/react/20/solid';

const GoogleSignUp = ({ googleToken, userData, onBackClick }) => {
  const navigate = useNavigate();
  const { googleSignUp } = useAuth();
  const [firstName, setFirstName] = useState(userData?.first_name || '');
  const [lastName, setLastName] = useState(userData?.last_name || '');
  const [email, setEmail] = useState(userData?.email || '');
  const [institution, setInstitution] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [scheduledOnboarding, setScheduledOnboarding] = useState(false);

  // Prevent scrolling on body when component is mounted
  useEffect(() => {
    // Save the original overflow style
    const originalStyle = window.getComputedStyle(document.body).overflow;
    // Prevent scrolling
    document.body.style.overflow = 'hidden';

    // Restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (!firstName || !lastName) {
      setError('Please fill in all required fields');
      return;
    }

    if (!institution) {
      setError('Institution name is required');
      return;
    }

    if (!scheduledOnboarding) {
      setError('Please schedule an onboarding session before continuing');
      return;
    }

    setIsLoading(true);

    try {
      const result = await googleSignUp({
        firstName,
        lastName,
        email,
        location: institution,
        tokenData: googleToken
      });

      if (result.error) {
        setError(result.message || 'An error occurred during registration');
        setIsLoading(false);
        return;
      }

      // Show signing in animation before redirecting
      setSigningIn(true);

      // Delay redirect to show the signing in animation
      setTimeout(() => {
        if (result.user.is_teacher) {
          navigate('/tclasses');
        } else {
          navigate('/sclasses');
        }
      }, 1500);
    } catch (err) {
      setError('An error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  const handleCalendlyClick = () => {
    window.open('https://calendly.com/maxbohun/gradewiz', '_blank');
  };

  return (
    // Override the parent container's width constraints with fixed positioning
    <div className="fixed inset-0 flex items-center justify-center bg-gray-50 z-50 p-4 overflow-hidden">
      <div className="w-full max-w-3xl bg-white rounded-lg shadow-md p-8 relative">
        {/* Loading overlay */}

        <button
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors duration-200"
          disabled={isLoading || signingIn}
          onClick={onBackClick}
        >
          <ChevronLeftIcon className="h-5 w-5 mr-1" />
          Back to Sign In
        </button>

        <h2 className="text-3xl font-extrabold text-gray-900">Complete Your Profile</h2>

        {error && (
          <div className="rounded-md bg-red-50 p-4 mb-6">
            <div className="text-sm text-red-800">{error}</div>
          </div>
        )}

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="first-name" className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <input
                id="first-name"
                name="first-name"
                type="text"
                autoComplete="given-name"
                required
                value={firstName}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-gold-500 focus:border-gold-500 sm:text-sm"
                disabled={isLoading || signingIn}
                onChange={e => setFirstName(e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="last-name" className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                id="last-name"
                name="last-name"
                type="text"
                autoComplete="family-name"
                required
                value={lastName}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-gold-500 focus:border-gold-500 sm:text-sm"
                disabled={isLoading || signingIn}
                onChange={e => setLastName(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              disabled
              value={email}
              className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-500 text-gray-900 bg-gray-50 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="institution" className="block text-sm font-medium text-gray-700 mb-1">
              Institution Name <span className="text-red-500">*</span>
            </label>
            <input
              id="institution"
              name="institution"
              type="text"
              placeholder="Enter your school or institution name"
              required
              value={institution}
              className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-gold-500 focus:border-gold-500 sm:text-sm"
              disabled={isLoading || signingIn}
              onChange={e => setInstitution(e.target.value)}
            />
          </div>

          <div className="pt-2">
            <div className="mb-4 p-3 border border-gray-300 rounded-md bg-gray-50">
              <button
                type="button"
                disabled={isLoading || signingIn}
                className="w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md bg-white transition-all duration-300 mb-2"
                style={{
                  color: '#4b5563',
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)'
                }}
                onClick={handleCalendlyClick}
                onMouseEnter={e => {
                  if (!isLoading && !signingIn) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 10px rgba(0, 0, 0, 0.15)';
                  }
                }}
                onMouseLeave={e => {
                  if (!isLoading && !signingIn) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.1)';
                  }
                }}
              >
                Schedule Onboarding Session
              </button>
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="scheduled-onboarding"
                    name="scheduled-onboarding"
                    type="checkbox"
                    checked={scheduledOnboarding}
                    disabled={isLoading || signingIn}
                    className="focus:ring-gold-500 h-4 w-4 text-gray-600 border-gray-300 rounded"
                    onChange={e => setScheduledOnboarding(e.target.checked)}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="scheduled-onboarding" className="font-medium text-gray-700">
                    I have scheduled an onboarding session
                  </label>
                </div>
              </div>
            </div>
            <button
              type="submit"
              disabled={isLoading || signingIn}
              className="w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-md text-white transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, #e4b17a, #c99b69)',
                boxShadow: '0 4px 12px rgba(228, 177, 122, 0.2)'
              }}
              onMouseEnter={e => {
                if (!isLoading && !signingIn) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(228, 177, 122, 0.3)';
                }
              }}
              onMouseLeave={e => {
                if (!isLoading && !signingIn) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(228, 177, 122, 0.2)';
                }
              }}
            >
              {isLoading
                ? 'Creating Account...'
                : signingIn
                  ? 'Signing you in...'
                  : 'Create Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GoogleSignUp;
